import React from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { cn } from "@/lib/cn";
import { isRecord } from "@/lib/records";

export interface ChatThreadMenuProps {
  readonly isStartingNewChat: boolean;
  readonly isOpen: boolean;
  readonly threadId: string | undefined;
  readonly onToggle: () => void;
  readonly onNewChat: () => void;
  readonly onSelectThread: (threadId: string) => void;
}

export function ChatThreadMenu({
  isStartingNewChat,
  isOpen,
  threadId,
  onToggle,
  onNewChat,
  onSelectThread,
}: ChatThreadMenuProps) {
  return (
    <div className="relative mx-auto mt-2 flex max-w-xl items-center justify-between gap-2">
      <button
        type="button"
        data-testid="new-chat-btn"
        onClick={onToggle}
        className="min-h-11 rounded-xl px-3 text-sm font-semibold text-accent active:scale-[0.98] active:bg-accent-light select-none"
        aria-expanded={isOpen}
      >
        {isStartingNewChat ? "Starting..." : "New chat"}
      </button>
      <span className="text-xs text-text-muted">Enter to send · Shift+Enter for a new line</span>
        {isOpen && (
          <ThreadHistoryErrorBoundary>
            <ThreadHistoryQuery threadId={threadId} onSelectThread={onSelectThread} onNewChat={onNewChat} />
          </ThreadHistoryErrorBoundary>
        )}
      </div>
    );
}

interface ThreadHistoryQueryProps {
  readonly threadId: string | undefined;
  readonly onNewChat: () => void;
  readonly onSelectThread: (threadId: string) => void;
}

function ThreadHistoryQuery({ threadId, onNewChat, onSelectThread }: ThreadHistoryQueryProps) {
  const threadHistory = useQuery(api.agent.listAppThreads, {});

  return (
    <div
      className="absolute bottom-12 left-0 right-0 rounded-2xl border border-border bg-surface p-2 shadow-lg"
      data-testid="thread-history"
    >
      <button
        type="button"
        onClick={onNewChat}
        className="flex min-h-11 w-full items-center rounded-xl px-3 text-left text-sm font-semibold text-accent active:bg-accent-light select-none"
      >
        Start a fresh conversation
      </button>
      {threadHistory === undefined ? (
        <p className="px-3 py-2 text-xs text-text-muted">Loading conversations...</p>
      ) : threadHistory.length > 0 ? (
        threadHistory.map((thread: unknown) => {
          if (!isRecord(thread) || typeof thread.threadId !== "string") return null;
          const selectedThreadId = thread.threadId;
          const title = typeof thread.title === "string" ? thread.title : "Conversation";
          return (
            <button
              type="button"
              key={selectedThreadId}
              onClick={() => onSelectThread(selectedThreadId)}
              className={cn(
                "flex min-h-11 w-full items-center rounded-xl px-3 text-left text-sm active:bg-muted select-none",
                selectedThreadId === threadId
                  ? "bg-accent-light font-semibold text-accent"
                  : "text-text",
              )}
            >
              <span className="line-clamp-1">{title}</span>
            </button>
          );
        })
      ) : (
        <p className="px-3 py-2 text-xs text-text-muted">
          Your recent conversations will appear here.
        </p>
      )}
    </div>
  );
}

type ThreadHistoryBoundaryState = {
  readonly hasError: boolean;
  readonly retryKey: number;
};

class ThreadHistoryErrorBoundary extends React.Component<
  { readonly children: React.ReactNode },
  ThreadHistoryBoundaryState
> {
  public state: ThreadHistoryBoundaryState = { hasError: false, retryKey: 0 };

  public static getDerivedStateFromError(): ThreadHistoryBoundaryState {
    return { hasError: true, retryKey: 0 };
  }

  public componentDidCatch(error: Error): void {
    console.warn("[Frontend][ChatThreadMenu] Thread history unavailable", {
      error: error.message,
    });
  }

  private handleRetry = (): void => {
    this.setState((current) => ({
      hasError: false,
      retryKey: current.retryKey + 1,
    }));
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="absolute bottom-12 left-0 right-0 rounded-2xl border border-border bg-surface p-3 shadow-lg"
          data-testid="thread-history-error"
          role="alert"
        >
          <p className="px-1 text-xs text-text-muted">Conversation history is temporarily unavailable.</p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="mt-2 flex min-h-11 w-full items-center justify-center rounded-xl bg-muted px-3 text-sm font-semibold text-accent active:scale-[0.98] active:bg-accent-light select-none"
          >
            Retry history
          </button>
        </div>
      );
    }

    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
